import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Crown, Sparkles, Check, ArrowRight, Headphones, ShieldCheck, BadgeCheck, Compass } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import Seo from '@/components/Seo';

const PERKS = [
  { icon: BadgeCheck, t: 'Unlimited Verified Listings', d: 'Post unlimited Sell / Rent listings — every property is RERA-screened by our team.' },
  { icon: Compass, t: 'Owner Connect · Unlimited', d: 'See verified phone numbers of property owners directly. No middlemen, no brokerage.' },
  { icon: Sparkles, t: 'Priority Search Placement', d: 'Your listings appear higher than non-premium across city pages and recommendations.' },
  { icon: Headphones, t: '24×7 Concierge Support', d: 'Dedicated WhatsApp line — India and NRI desk. Site visits, paperwork, legal — all covered.' },
  { icon: ShieldCheck, t: 'Legal & Stamp Duty Help', d: 'Get help on agreement drafting, stamp-duty, registration timelines, and TDS for NRIs.' },
  { icon: Crown, t: 'Founders' + "'" + ' Badge', d: 'A subtle gold "Founders Member" badge on your profile — visible across the platform.' },
];

const PremiumPage = () => {
  const { user } = useAuth();
  const [expiry, setExpiry] = useState('');

  useEffect(() => {
    if (user?.premium_expires_at) {
      try {
        setExpiry(new Date(user.premium_expires_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' }));
      } catch { /* ignore */ }
    }
  }, [user]);

  // Existing premium-success URLs etc still hit this page; logged-out users just see the offer.
  if (typeof window !== 'undefined' && window.location.pathname === '/premium-success') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <>
      <Seo
        title="Founders' First Month Free · Premium Membership"
        description="Premium membership at Bharat Estate is free for the first month — auto-activated on signup, chargeable afterwards."
        path="/premium"
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0F172A] text-white" data-testid="premium-page">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <span className="be-aurora-blob be-aurora-blob--saffron" />
          <span className="be-aurora-blob be-aurora-blob--green" />
          <span className="be-aurora-blob be-aurora-blob--gold" />
          <div className="absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 30%, #D4AF37 0px, transparent 1.2px), radial-gradient(circle at 70% 60%, #ffffff 0px, transparent 1.2px)',
              backgroundSize: '24px 24px, 32px 32px',
            }}
          />
        </div>

        <div className="relative container-luxury py-16 md:py-24">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37] text-xs uppercase tracking-[0.18em] mb-6">
                <Sparkles className="h-3.5 w-3.5" /> Founders' Launch Offer
              </div>

              <h1 className="font-['Plus_Jakarta_Sans'] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5">
                Premium is <span className="text-[#D4AF37]">free for the first month</span>.<br />
                <span className="text-white/85">For every member.</span>
              </h1>
              <p className="text-white/75 text-lg max-w-xl leading-relaxed mb-3">
                First month free for every new user — after that, Premium is chargeable via our monthly plans.
                No card. No catch. Activated the moment you sign in.
              </p>

              {user?.is_premium && (
                <div className="mt-7 bg-gradient-to-br from-[#D4AF37]/15 to-transparent border border-[#D4AF37]/30 rounded-2xl p-5 max-w-xl">
                  <p className="text-xs uppercase tracking-widest text-[#D4AF37] mb-1">Your status</p>
                  <p className="text-white text-xl font-semibold">You are a Founders Member <Crown className="inline h-5 w-5 text-[#D4AF37] -mt-1 ml-1" /></p>
                  {expiry && <p className="text-white/65 text-sm mt-1">Valid through <span className="text-white font-medium">{expiry}</span></p>}
                </div>
              )}

              {!user && (
                <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md">
                  <Link to="/register" className="bg-[#D4AF37] hover:bg-[#b8932f] text-[#0F172A] font-bold px-6 py-3 rounded-xl flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]" data-testid="premium-cta-register">
                    Claim My Free Month <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link to="/login" className="text-white/85 hover:text-white border border-white/15 hover:border-white/40 font-medium px-6 py-3 rounded-xl flex items-center justify-center transition-colors">
                    I already have an account
                  </Link>
                </div>
              )}
            </div>

            {/* The "card" */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="relative aspect-[1.586/1] max-w-md mx-auto rounded-2xl overflow-hidden shadow-2xl shadow-[#D4AF37]/20 border border-[#D4AF37]/30">
                <div className="absolute inset-0 bg-gradient-to-br from-[#1E293B] via-[#0F172A] to-[#000]" />
                <div className="absolute -top-12 -left-10 h-48 w-48 rounded-full bg-[#D4AF37]/30 blur-3xl" />
                <div className="absolute -bottom-12 -right-10 h-48 w-48 rounded-full bg-[#FF9933]/25 blur-3xl" />
                <div className="absolute inset-0 opacity-[0.07]"
                  style={{
                    backgroundImage: 'radial-gradient(circle at 50% 50%, #D4AF37 0px, transparent 1.5px)',
                    backgroundSize: '18px 18px',
                  }}
                />
                <div className="relative h-full p-6 flex flex-col justify-between text-white">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-white/55">Bharat Estate</p>
                      <p className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mt-1">Founders Membership</p>
                    </div>
                    <Crown className="h-7 w-7 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/45">Member</p>
                    <p className="text-lg font-semibold tracking-wide">{user?.name || 'New Member'}</p>
                    <div className="flex items-end justify-between mt-3">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-white/45">Valid through</p>
                        <p className="text-sm font-mono tracking-wider">{expiry || '1 Month From Signup'}</p>
                      </div>
                      <p className="font-['Plus_Jakarta_Sans'] text-lg italic text-[#D4AF37]">Ultimate</p>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-center text-white/45 text-xs mt-3">A free, lifetime-style upgrade for our first 10,000 members</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PERKS */}
      <section className="section-gap bg-[#F9F9F7]">
        <div className="container-luxury">
          <div className="text-center mb-12">
            <p className="caption mb-3">Everything you get</p>
            <h2 className="heading-secondary text-[#0F172A]">Built for serious buyers, sellers & NRIs</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PERKS.map((p, i) => (
              <motion.div
                key={p.t}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="bg-white border border-black/5 hover:border-[#D4AF37]/40 hover:-translate-y-1 transition-all rounded-2xl p-6"
              >
                <div className="h-11 w-11 rounded-xl bg-[#0F172A] text-[#D4AF37] flex items-center justify-center mb-4">
                  <p.icon className="h-5 w-5" />
                </div>
                <p className="font-semibold text-[#0F172A] text-[15px] mb-2">{p.t}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-gap bg-white">
        <div className="container-luxury max-w-3xl">
          <div className="text-center mb-10">
            <p className="caption mb-3">Frequently asked</p>
            <h2 className="heading-secondary text-[#0F172A]">Questions, answered</h2>
          </div>
          <div className="space-y-4">
            {[
              { q: 'Is there any hidden charge?', a: 'No. Premium is free for the first month for every member. No card on file during the trial; after that you choose a paid plan to continue.' },
              { q: 'Why is Bharat Estate giving this away?', a: 'We’re building India’s most trusted real-estate community. Our founding members get the platform free in exchange for using it, sharing feedback, and helping us grow.' },
              { q: 'What happens after the first month?', a: 'Premium becomes chargeable — pick a monthly plan to keep listing and contacting owners. We never auto-charge; you pay only when you choose to continue.' },
              { q: 'I signed up a while ago. Am I still eligible?', a: 'Yes — every existing user has been automatically upgraded. Just sign in and the Founders badge appears on your dashboard.' },
            ].map((f) => (
              <details key={f.q} className="group bg-[#F9F9F7] rounded-xl p-5 cursor-pointer border border-transparent open:border-[#D4AF37]/40 transition-colors">
                <summary className="font-medium text-[#0F172A] flex items-center justify-between list-none">
                  {f.q}
                  <span className="h-6 w-6 rounded-full bg-white border border-black/10 flex items-center justify-center group-open:rotate-45 transition-transform">
                    <span className="text-[#D4AF37]">+</span>
                  </span>
                </summary>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white">
        <div className="container-luxury py-14 text-center">
          <Crown className="h-10 w-10 text-[#D4AF37] mx-auto mb-4" />
          <h3 className="font-['Plus_Jakarta_Sans'] text-3xl md:text-4xl mb-3">Your free month is waiting.</h3>
          <p className="text-white/65 max-w-lg mx-auto mb-7">
            One sign-in and the Founders Membership · Ultimate plan is yours — free for your first month, then chargeable.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {user?.is_premium ? (
              <Link to="/dashboard" className="bg-[#D4AF37] hover:bg-[#b8932f] text-[#0F172A] font-bold px-7 py-3 rounded-xl inline-flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]">
                Go to my Dashboard <ArrowRight className="h-4 w-4" />
              </Link>
            ) : (
              <Link to="/register" className="bg-[#D4AF37] hover:bg-[#b8932f] text-[#0F172A] font-bold px-7 py-3 rounded-xl inline-flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]">
                Claim My Free Month <ArrowRight className="h-4 w-4" />
              </Link>
            )}
            <Link to="/properties" className="text-white/85 hover:text-white border border-white/15 hover:border-white/40 font-medium px-7 py-3 rounded-xl inline-flex items-center justify-center transition-colors">
              Browse properties
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default PremiumPage;
